<script lang="ts" setup>
import AdaptiveDialog from '../AdaptiveDialog.vue'
import Button from 'primevue/button'
import IftaLabel from 'primevue/iftalabel'
import Fieldset from 'primevue/fieldset'
import Select from 'primevue/select'
import SrInputNumber from '@/components/SrInputNumber.vue'
import InputText from 'primevue/inputtext'
import { Form, FormField, type FormInstance, type FormSubmitEvent } from '@primevue/forms'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { capitalize } from 'lodash'
import z from 'zod'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { i18n } from '@/i18n'
import { useProfiles } from '@/composable/useMultiProfiles'
import { AutoTradeTriggerService } from '@/services/auto-trade-trigger-service'
import { AVAILABLE_SPOT_EXCHANGES, AVAILABLE_SWAP_EXCHANGES, QUOTE_TYPES } from '@/utils/consts'
import type { AutoTradeTriggerCreate } from '@/services/auto-trade-trigger-service/types'
import type { MarketType } from '@/utils/types'
import { useMyToast } from '@/composable/useMyToast'

const { t } = i18n.global
const { addToast } = useMyToast()

const props = defineProps<{
  exBuy?: string | null
  exSell?: string | null
  asset?: string | null
  sellAsset?: string | null
  marketType?: MarketType | null
  amount?: number | null
  quoteBuy?: string
  quoteSell?: string
}>()

const visible = ref(false)
const loading = ref(false)

const emit = defineEmits<{
  (e: 'created'): void
}>()

const settingsForm = useTemplateRef<FormInstance>('settingsForm')

const hasPresetParams = computed(() =>
  !!(props.exBuy && props.exSell && props.asset && props.marketType)
)

const cacheKeyPostfix = computed(() =>
  hasPresetParams.value
    ? `${props.exBuy}/${props.exSell}/${props.asset}/${props.marketType}`.toLocaleLowerCase()
    : 'default'
)

const { toggleProfile, profiles, enabledProfiles } = useProfiles(
  computed(() => cacheKeyPostfix.value),
  'cex_cex'
)

const marginModes = [
  { label: t('marginModes.isolated'), value: 'isolated' },
  { label: t('marginModes.cross'), value: 'cross' },
]

const marketTypes = [
  { label: t("swapSwapLabel"), value: 'swap-swap' },
  { label: t('spotSwapLabel'), value: 'spot-swap' },
]

const initValues = computed(() => ({
  ex_buy: props.exBuy ?? '',
  ex_sell: props.exSell ?? '',
  asset: props.asset ?? '',
  sell_asset: props.sellAsset ?? '',
  market_type: (props.marketType === 'swap' ? 'swap-swap' : props.marketType) ?? 'swap-swap',
  quote_buy: props.quoteBuy ?? 'USDT',
  quote_sell: props.quoteSell ?? 'USDT',
  entry_spread: null,
  exit_spread: null,
  amount: props.amount ?? null,
  leverage: 1,
  slippage: 1,
  margin_mode: 'isolated',
  profile_num: 0,
}))

const toggle = async () => {
  visible.value = !visible.value
  if (visible.value) {
    enabledProfiles.value = [0]
  }
}

defineExpose({
  toggle,
})

watch(
  () => props.amount,
  (v) => {
    settingsForm.value?.setFieldValue('amount', v?.toString())
  },
)

const currentMarketType = computed(() => {
  const state = settingsForm.value?.getFieldState?.('market_type')
  const mt = state?.value
  return mt?.value ?? mt ?? 'swap-swap'
})

const buyExchanges = computed(() => {
  const mt = currentMarketType.value
  const source = mt === 'spot-swap' ? AVAILABLE_SPOT_EXCHANGES : AVAILABLE_SWAP_EXCHANGES
  return source.map((ex) => ({ label: capitalize(ex), value: ex.toLowerCase() }))
})

const sellExchanges = computed(() => {
  const mt = currentMarketType.value
  const source = mt === 'spot-swap' ? AVAILABLE_SWAP_EXCHANGES : AVAILABLE_SWAP_EXCHANGES
  return source.map((ex) => ({ label: capitalize(ex), value: ex.toLowerCase() }))
})

const preprocessNum = (val: unknown) => {
  if (val === '' || val === null || val === undefined) return undefined
  const n = Number(val)
  return isNaN(n) ? undefined : n
}

const schema = z.object({
  ex_buy: z.string().min(1, { message: t('required') }),
  ex_sell: z.string().min(1, { message: t('required') }),
  asset: z.string().min(1, { message: t('required') }),
  sell_asset: z.string().optional().nullable(),
  market_type: z.string().min(1, { message: t('required') }),
  entry_spread: z.preprocess(preprocessNum, z.any().refine((v) => typeof v === 'number' && !isNaN(v) && v > -200 && v < 200, { message: t('modal.autoTradeTrigger.errors.entrySpreadRequired') })),
  exit_spread: z.preprocess(preprocessNum, z.any().refine((v) => typeof v === 'number' && !isNaN(v) && v > -200 && v < 200, { message: t('modal.autoTradeTrigger.errors.exitSpreadRequired') })),
  amount: z.preprocess(preprocessNum, z.any().refine((v) => typeof v === 'number' && !isNaN(v) && v >= 10 && v <= 1000, { message: t('modal.autoTradeTrigger.errors.amountRange') })),
  leverage: z.preprocess(preprocessNum, z.any().refine((v) => typeof v === 'number' && !isNaN(v) && v >= 1 && v <= 10, { message: t('modal.autoTradeTrigger.errors.leverage') })),
  slippage: z.preprocess(preprocessNum, z.any().refine((v) => typeof v === 'number' && !isNaN(v) && v >= 0, { message: t('modal.autoTradeTrigger.errors.slippage') })),
  margin_mode: z.string().optional(),
  quote_buy: z.string().optional(),
  quote_sell: z.string().optional(),
})

const resolver = zodResolver(schema)

const marketOrder = ref(false)
const closeOnFail = ref(false)
const oneShot = ref(true)

function fieldStr(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object' && 'value' in v) {
    const inner = (v as Record<string, unknown>).value
    return typeof inner === 'string' ? inner : String(inner ?? '')
  }
  return v != null ? String(v) : ''
}

function fieldTrim(v: unknown): string {
  return fieldStr(v).trim()
}

function fieldOpt(v: unknown): string | null {
  const s = fieldTrim(v)
  return s || null
}

const onSubmit = async (e: FormSubmitEvent) => {
  if (!e.valid) return
  if (!enabledProfiles.value?.length) {
    addToast({ severity: 'warn', summary: t('warn'), detail: t('selectProfile'), life: 3000 })
    return
  }

  const values = e.values as Record<string, unknown>

  loading.value = true
  try {
    const entrySpread = Number(values.entry_spread)
    const exitSpread = Number(values.exit_spread)
    const slippage = Number(values.slippage)

    for (const profileNum of enabledProfiles.value) {
      const payload: AutoTradeTriggerCreate = {
        ex_buy: fieldStr(values.ex_buy) || '',
        ex_sell: fieldStr(values.ex_sell) || '',
        asset: fieldTrim(values.asset).toUpperCase(),
        sell_asset: fieldOpt(values.sell_asset),
        market_type: fieldStr(values.market_type) || 'swap-swap',
        entry_spread: entrySpread,
        exit_spread: exitSpread,
        amount: Number(values.amount),
        leverage: Number(values.leverage),
        slippage: slippage,
        margin_mode: fieldStr(values.margin_mode) || 'isolated',
        market_order: marketOrder.value,
        close_on_fail: closeOnFail.value,
        quote_buy: (fieldStr(values.quote_buy) || 'USDT').toUpperCase(),
        quote_sell: (fieldStr(values.quote_sell) || 'USDT').toUpperCase(),
        profile_num: profileNum,
        one_shot: oneShot.value,
      }
      await AutoTradeTriggerService.createTrigger(payload)
    }
    addToast({
      severity: 'success',
      summary: t('success'),
      detail: t('modal.autoTradeTrigger.created'),
      life: 3000,
    })
    emit('created')
    visible.value = false
    settingsForm.value?.reset?.()
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosErr = err as { response?: { data?: { detail?: unknown } } }
      const detailData = axiosErr.response?.data?.detail
      if (Array.isArray(detailData) && detailData.length > 0) {
        const first = detailData[0] as Record<string, unknown>
        const type = typeof first.type === 'string' ? first.type : undefined
        if (type) {
          const translated = t(`modal.autoTradeTrigger.errors.${type}`, { default: null })
          if (translated !== null && translated !== `modal.autoTradeTrigger.errors.${type}`) {
            addToast({ severity: 'error', summary: t('error'), detail: translated, life: 5000 })
            return
          }
        }
        if (typeof first.msg === 'string') {
          const msgKey = first.msg.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
          const msgTranslated = t(`modal.autoTradeTrigger.errors.${msgKey}`, { default: null })
          if (msgTranslated !== null && msgTranslated !== `modal.autoTradeTrigger.errors.${msgKey}`) {
            addToast({ severity: 'error', summary: t('error'), detail: msgTranslated, life: 5000 })
            return
          }
          addToast({ severity: 'error', summary: t('error'), detail: first.msg.replace(/^Value error,\s*/i, ''), life: 5000 })
          return
        }
        // canonical fallback matching useTrade.ts pattern
        const errorType = type ?? 'unexceptedError'
        const errorParams = (Array.isArray(first.params) ? first.params : []) as Array<string | number>
        const fallback = t(`errors.${errorType}`, errorParams)
        addToast({ severity: 'error', summary: t('error'), detail: fallback, life: 5000 })
        return
      } else if (typeof detailData === 'string') {
        addToast({ severity: 'error', summary: t('error'), detail: detailData, life: 5000 })
        return
      }
    }
    addToast({ severity: 'error', summary: t('error'), detail: t('modal.autoTradeTrigger.createError'), life: 5000 })
  } finally {
    loading.value = false
  }
}

const handleCreate = async () => {
  const result = await settingsForm.value?.validate()
  const hasErrors = result?.errors && Object.keys(result.errors).length > 0
  if (hasErrors) {
    const errors = result?.errors as Record<string, { message?: string }[]> | undefined

    const fieldLabels: Record<string, string> = {
      ex_buy: t('exBuy'),
      ex_sell: t('exSell'),
      asset: t('asset'),
      market_type: t('marketType'),
      entry_spread: t('modal.autoTradeTrigger.entrySpread'),
      exit_spread: t('modal.autoTradeTrigger.exitSpread'),
      amount: t('usdtAmount'),
      leverage: t('modal.createSwapOrders.leverage'),
    }

    const requiredText = t('required')

    const messages = errors
      ? Object.entries(errors)
        .flatMap(([field, fieldErrors]) => {
          const label = fieldLabels[field] || field
          return fieldErrors.map((e) => {
            const msg = e.message || ''
            if (msg === requiredText) {
              return t('fieldRequired', { field: label })
            }
            return `${label}: ${msg}`
          })
        })
        .filter((msg, idx, arr) => arr.indexOf(msg) === idx)
        .join('\n')
      : ''

    addToast({
      severity: 'error',
      summary: t('error'),
      detail: messages || t('modal.autoTradeTrigger.createError'),
      life: 5000,
    })
    return
  }
  settingsForm.value?.submit()
}

const uppercaseField = (fieldName: string) => {
  const state = settingsForm.value?.getFieldState(fieldName)
  const val = fieldStr(state?.value).toUpperCase()
  settingsForm.value?.setFieldValue(fieldName, val)
}

const onAssetInput = () => {
  uppercaseField('asset')
  const assetState = settingsForm.value?.getFieldState('asset')
  settingsForm.value?.setFieldValue('sell_asset', fieldStr(assetState?.value))
}

</script>

<template>
  <AdaptiveDialog v-model:visible="visible" pt:root="w-full lg:w-[500px]" pt:content="p-2" pt:header="px-4 py-1"
    :header="$t('modal.autoTradeTrigger.title')">
    <Form ref="settingsForm" :resolver="resolver" :initialValues="initValues" @submit="onSubmit"
      class="grid grid-cols-1 gap-3">

      <!-- Exchanges -->
      <div class="grid grid-cols-2 gap-2">
        <FormField name="ex_buy">
          <IftaLabel>
            <Select :options="buyExchanges" optionLabel="label" optionValue="value" fluid :placeholder="$t('exBuy')"
              :disabled="hasPresetParams" />
            <label>{{ $t('exBuy') }}</label>
          </IftaLabel>
        </FormField>
        <FormField name="ex_sell">
          <IftaLabel>
            <Select :options="sellExchanges" optionLabel="label" optionValue="value" fluid :placeholder="$t('exSell')"
              :disabled="hasPresetParams" />
            <label>{{ $t('exSell') }}</label>
          </IftaLabel>
        </FormField>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <FormField name="asset">
          <IftaLabel>
            <InputText type="text" @input="onAssetInput" fluid :placeholder="$t('buyAsset')" :disabled="hasPresetParams"
              autocomplete="off" />
            <label>{{ $t('buyAsset') }}</label>
          </IftaLabel>
        </FormField>
        <FormField name="sell_asset">
          <IftaLabel>
            <InputText type="text" @input="uppercaseField('sell_asset')" fluid :placeholder="$t('sellAsset')"
              :disabled="hasPresetParams" autocomplete="off" />
            <label>{{ $t('sellAsset') }}</label>
          </IftaLabel>
        </FormField>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <FormField name="quote_buy">
          <IftaLabel>
            <Select :options="QUOTE_TYPES" optionLabel="label" optionValue="value" fluid :disabled="hasPresetParams"
              :placeholder="$t('quoteBuy')" />
            <label>{{ $t('quoteBuy') }}</label>
          </IftaLabel>
        </FormField>
        <FormField name="quote_sell">
          <IftaLabel>
            <InputText type="text" fluid :placeholder="$t('quoteSell')" :disabled="hasPresetParams" />
            <label>{{ $t('quoteSell') }}</label>
          </IftaLabel>
        </FormField>
      </div>

      <FormField name="market_type">
        <IftaLabel>
          <Select :options="marketTypes" optionLabel="label" optionValue="value" fluid :placeholder="$t('marketType')"
            :disabled="hasPresetParams" />
          <label>{{ $t('marketType') }}</label>
        </IftaLabel>
      </FormField>

      <!-- Trigger spreads -->
      <div class="grid grid-cols-2 gap-2">
        <FormField name="entry_spread">
          <IftaLabel>
            <SrInputNumber type="text" fluid :placeholder="$t('modal.autoTradeTrigger.entrySpread')" suffix="%"
              :min="0.01" :maxFractionDigits="2" />
            <label>{{ $t('modal.autoTradeTrigger.entrySpread') }}</label>
          </IftaLabel>
        </FormField>
        <FormField name="exit_spread">
          <IftaLabel>
            <SrInputNumber type="text" fluid :placeholder="$t('modal.autoTradeTrigger.exitSpread')" suffix="%"
              :maxFractionDigits="2" />
            <label>{{ $t('modal.autoTradeTrigger.exitSpread') }}</label>
          </IftaLabel>
        </FormField>
      </div>

      <!-- Объём -->
      <FormField name="amount">
        <IftaLabel>
          <SrInputNumber type="text" fluid :placeholder="$t('usdtAmount')" prefix="$" />
          <label>{{ $t('usdtAmount') }}</label>
        </IftaLabel>
      </FormField>

      <!-- Плечо -->
      <FormField name="leverage">
        <IftaLabel>
          <SrInputNumber type="text" fluid :placeholder="$t('modal.createSwapOrders.leverage')" :min="1" :max="125" />
          <label>{{ $t('modal.createSwapOrders.leverage') }}</label>
        </IftaLabel>
      </FormField>

      <!-- Проскальзывание -->
      <FormField name="slippage">
        <IftaLabel>
          <SrInputNumber type="text" fluid :placeholder="$t('modal.createSwapOrders.slippage')" :min="-20" :max="20"
            :disabled="marketOrder" />
          <label>{{ $t('modal.createSwapOrders.slippage') }}</label>
        </IftaLabel>
      </FormField>

      <!-- Режим маржи -->
      <FormField name="margin_mode">
        <IftaLabel>
          <Select :options="marginModes" optionLabel="label" optionValue="value" fluid
            :placeholder="$t('modal.createSwapOrders.marginMode')" />
          <label>{{ $t('modal.createSwapOrders.marginMode') }}</label>
        </IftaLabel>
      </FormField>

      <Fieldset :legend="$t('modal.createSwapOrders.anotherSettings')">

        <div class="grid grid-cols-1 gap-2">
          <!-- <Button class="col-span-1" :icon="closeOnFail ? 'pi pi-check-square' : 'pi pi-stop'"
            @click="closeOnFail = !closeOnFail" severity="success" :variant="closeOnFail ? undefined : 'outlined'"
            :label="$t('modal.createSwapOrders.closeOnFail')">
          </Button> -->
          <Button class="col-span-1" :icon="marketOrder ? 'pi pi-check-square' : 'pi pi-stop'"
            @click="marketOrder = !marketOrder" severity="success" :variant="marketOrder ? undefined : 'outlined'"
            :label="$t('modal.createSwapOrders.marketOrder')">
          </Button>
          <Button class="col-span-1" :icon="oneShot ? 'pi pi-check-square' : 'pi pi-stop'" @click="oneShot = !oneShot"
            severity="success" :variant="oneShot ? undefined : 'outlined'"
            :label="$t('modal.autoTradeTrigger.oneShot')">
          </Button>
        </div>
      </Fieldset>

      <!-- Профили -->
      <Fieldset v-if="profiles.length > 1" :legend="$t('modal.createSwapOrders.profiles')">
        <div class="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-5 gap-1">

          <Button @click="toggleProfile(idx)" severity="success"
            :variant="enabledProfiles.includes(idx) ? undefined : 'outlined'" size="small" fluid v-for="idx in profiles"
            :key="idx">
            <i class="pi"
              :class="{ 'pi-check-square': enabledProfiles.includes(idx), 'pi-stop': !enabledProfiles.includes(idx) }"></i>
            <span>#{{ idx + 1 }}</span>
          </Button>

        </div>
      </Fieldset>

      <Button type="button" :loading="loading" severity="success" variant="outlined"
        :label="$t('modal.autoTradeTrigger.create')" @click="handleCreate" />
    </Form>
  </AdaptiveDialog>
</template>
